import usePayment from "hooks/usePayment";
import { useMemo } from "react";
import { toast } from "react-toastify";
import { translateErrors } from "utils";
import { GetAllDebitsRequest } from "views/Partner/providers/dto/get-debits.dto";
import {
  useGetDebitsPublicDepartment,
} from "views/Partner/providers/get-debits-public-department.provider";
import { useGetDebits } from "views/Partner/providers/get-debits.provider";
import { ordenarDebits } from "../utils";

type PublicDepartmentRequest = Pick<
  GetAllDebitsRequest,
  | "document"
  | "municipal_registration"
  | "property_registration_number"
  | "id_establishment"
  | "public_department"
>;

interface useGetDebitsManagerProps {
  license_plate: string;
  isPublicDepartment: boolean;
  setVariablesRequest?: (data: GetAllDebitsRequest | PublicDepartmentRequest) => void;
}

export function useGetDebitsManager({ license_plate, isPublicDepartment, setVariablesRequest }: useGetDebitsManagerProps) {
  const { setDataPartnerSearch, detran, dataPaymentBillet, setPaymentBilletData } = usePayment();

  const queryDebitsPublicDepartment = useGetDebitsPublicDepartment({
    onMutate(variables) {
      setVariablesRequest?.(variables as PublicDepartmentRequest);
    },
    onSuccess: (data) => {
      if (!data?.debits?.length) {
        toast.warning("Não foram encontrados débitos pendentes.");
        return;
      }

      setDataPartnerSearch({
        document: data?.document ?? data?.municipal_registration ?? data?.property_registration_number,
        parceiro: data?.public_department,
      });

      setPaymentBilletData({
        ...dataPaymentBillet,
        debits: ordenarDebits(data.debits || []),
      });
    },
    onError: (error) => {
      const message = Array.isArray(error.response?.data?.message)
        ? error.response?.data?.message?.[0]
        : error.response?.data?.message;

      toast.warning(
        translateErrors(
          message || "ocorreu um erro ao buscar os débitos. Tente novamente."
        )
      );
    },
    retry: false
  });

  const queryDebits = useGetDebits({
    onMutate(variables) {
      setVariablesRequest?.(variables as GetAllDebitsRequest);
    },
    onSuccess: (data) => {
      if (!data?.debits?.length) {
        toast.warning("Não foram encontrados débitos para este veículo.");
        return;
      }

      setDataPartnerSearch({
        license_plate: data?.license_plate ?? license_plate,
        renavam: data?.renavam,
        id_parceiro: detran?.id_partner,
        parceiro: `Detran ${queryDebits?.variables?.state ?? detran?.state}`,
      });

      setPaymentBilletData({
        ...dataPaymentBillet,
        debits: data.debits,
      });
    },
    onError: (error) => {
      const message = Array.isArray(error.response?.data?.message)
        ? error.response?.data?.message?.[0]
        : error.response?.data?.message;

      toast.warning(
        translateErrors(
          message || "ocorreu um erro ao buscar os débitos. Tente novamente."
        )
      );
    },
    retry: false
  });

  const activeMutationObject = isPublicDepartment ? queryDebitsPublicDepartment : queryDebits;

const typedMutate = useMemo(() => {
  if (isPublicDepartment) {
    return activeMutationObject.mutate as (variables: PublicDepartmentRequest) => void;
  } else {
    return activeMutationObject.mutate as (variables: GetAllDebitsRequest) => void;
  }
}, [isPublicDepartment, queryDebitsPublicDepartment.mutate, queryDebits.mutate]);

  return {
    ...activeMutationObject,
    mutate: typedMutate,
  };
}
